import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();

export interface UtbDutyLine {
  code: string;
  description: string;
  base: number;
  rate: number;
  surety: number;
  payment: number;
}

export interface UtbParsedResult {
  btwBijInvoer: number;
  invoerrechten: number;
  lines: UtbDutyLine[];
  rawText?: string;
}

function parseNlNumber(s: string): number {
  const cleaned = s.replace(/\./g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
}

async function extractTextFromPdfBlob(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const pageTexts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    // Group text items by Y position to reconstruct lines
    const itemsByY = new Map<number, Array<{ x: number; str: string }>>();
    for (const item of content.items) {
      if (!("str" in item) || !item.str.trim()) continue;
      const y = Math.round((item as { transform: number[] }).transform[5]);
      if (!itemsByY.has(y)) itemsByY.set(y, []);
      itemsByY.get(y)!.push({
        x: (item as { transform: number[] }).transform[4],
        str: item.str,
      });
    }

    // Sort by Y (descending = top to bottom) then X (left to right)
    const sortedYs = [...itemsByY.keys()].sort((a, b) => b - a);
    for (const y of sortedYs) {
      const items = itemsByY.get(y)!.sort((a, b) => a.x - b.x);
      pageTexts.push(items.map((it) => it.str).join(" "));
    }
  }

  return pageTexts.join("\n");
}

/**
 * Matches duty lines like:
 * A00 - CUSTOMS DUTIES ON INDUSTRIAL PRODUCTS 7.028,52 EUR 0.000% 0,00 EUR 0,00 EUR
 * B00 - TAXCODE VAT 7.028,52 EUR 21.000% 1.475,99 EUR 0,00 EUR
 * Columns after rate: Zekerheid (surety), Verschuldigd (payment).
 * Invoice amounts use Verschuldigd only.
 */
const DUTY_LINE_RE =
  /([AB]\d{2})\s*[-–]\s*(.+?)\s+([\d.,]+)\s*EUR\s+([\d.,]+)\s*%\s+([\d.,]+)\s*EUR\s+([\d.,]+)\s*EUR/g;

export async function parseUtbPdf(blob: Blob): Promise<UtbParsedResult> {
  const text = await extractTextFromPdfBlob(blob);
  const lines: UtbDutyLine[] = [];
  let btwBijInvoer = 0;
  let invoerrechten = 0;

  for (const m of text.matchAll(DUTY_LINE_RE)) {
    const [, code, description, baseStr, rateStr, suretyStr, paymentStr] = m;
    const surety = parseNlNumber(suretyStr);
    // Invoice lines use Verschuldigd (payment) only — never Zekerheid (surety/TTW).
    const payment = parseNlNumber(paymentStr);

    lines.push({
      code,
      description: description.trim(),
      base: parseNlNumber(baseStr),
      rate: parseNlNumber(rateStr),
      surety,
      payment,
    });

    if (code.startsWith("B")) {
      btwBijInvoer += payment;
    } else if (code.startsWith("A")) {
      invoerrechten += payment;
    }
  }

  if (lines.length === 0) {
    console.log("UTB PDF extracted text (no duty lines matched):", text.substring(0, 2000));
  }

  return {
    btwBijInvoer: Math.round(btwBijInvoer * 100) / 100,
    invoerrechten: Math.round(invoerrechten * 100) / 100,
    lines,
    rawText: lines.length === 0 ? text : undefined,
  };
}
